import { useAuth } from '@/auth/useAuth'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { useAssetQaVisible } from '@/hooks/useAssetQaVisible'
import { effectiveSettingsState } from '@/state/workflowSettings'

export interface AssetReviewPermissions {
  isInternalTeam: boolean
  isDefaultReviewer: boolean
  isWorkflowParticipant: boolean
  canEditInWebster: boolean
  canCreateAsset: boolean
  canSignOff: boolean
  canChangeStatus: boolean
  canReopen: boolean
  canUploadVersion: boolean
  canEditName: boolean
  canDeleteAsset: boolean
  canOverrideQaFinding: boolean
  isCommentsDisabled: (isSignedOff: boolean) => boolean
  requireSignOffConfirmation: boolean
  settingsLoaded: boolean
}

/**
 * Who may do what on the asset review surface, for the current user.
 *
 * Two different questions decide it, and keeping them apart is the point:
 *   - **Approving** — change status, comment — is decided by *list membership* alone, Default
 *     Reviewers or Internal Team, matching the endpoints behind them. Deliberately role-free, so a
 *     workspace Viewer named as a reviewer can move creative they are not allowed to change
 *     (ORB-666). Sign-off is narrower and always was: Default Reviewers only. Reopen narrows to the
 *     Internal Team when `InternalOnlyReopen` is on, and is role-gated there because that is the
 *     edit side of the list.
 *   - **Editing** — Webster, create, rename, upload a version, delete — needs list membership AND
 *     the workspace edit role. Membership alone used to be enough, so an editor later demoted to
 *     Viewer kept every edit affordance on screen while the API 403'd them.
 *
 * Ids default to the current route; pass them where the relevant workspace is not the one in the
 * URL. Every flag is false until effective settings load, which fails closed.
 */
export function useAssetReviewPermissions(
  companyIdOverride?: string,
  workspaceIdOverride?: string
): AssetReviewPermissions {
  const { userProfile } = useAuth()
  const { canEdit } = useWorkspaceCapabilities(companyIdOverride, workspaceIdOverride)
  const qaVisible = useAssetQaVisible()
  const settings = effectiveSettingsState.value.settings
  const settingsLoaded = !effectiveSettingsState.value.loading && settings !== null

  const userId = userProfile?.Id
  const isInternalTeam = settingsLoaded && !!userId &&
    (settings.InternalTeamUsers?.some(u => u.Id === userId) ?? false)
  const isDefaultReviewer = settingsLoaded && !!userId &&
    (settings.DefaultReviewerUsers?.some(u => u.Id === userId) ?? false)

  // The internal team produce the work, so the edit affordances are theirs — but only while they
  // still hold the role. Stricter than the API, which cannot read roles out of workflow settings;
  // the settings upsert validator is what keeps new Viewers off that list server-side.
  const canEditAssets = isInternalTeam && canEdit

  const canReopen = settings?.InternalOnlyReopen
    ? canEditAssets
    : canEditAssets || isDefaultReviewer

  const isWorkflowParticipant = isInternalTeam || isDefaultReviewer

  const isCommentsDisabled = (isSignedOff: boolean) =>
    !isWorkflowParticipant || (isSignedOff && settings?.AllowCommentsAfterSignOff === false)

  return {
    isInternalTeam,
    isDefaultReviewer,
    isWorkflowParticipant,
    canEditInWebster: canEditAssets,
    canCreateAsset: canEditAssets,
    canSignOff: isDefaultReviewer,
    // Either list, matching ChangeAssetStatus itself. Reviewer-only would strand the round trip:
    // the reviewer sends work back as Feedback Submitted and the internal team who rework it have
    // no way to hand it back, which is the half-loop this ticket exists to close (ORB-666).
    canChangeStatus: isWorkflowParticipant,
    canReopen,
    canUploadVersion: canEditAssets,
    canEditName: canEditAssets,
    canDeleteAsset: canEditAssets,
    // The workspace edit role alone, matching the endpoint's own gate, and deliberately NOT
    // narrowed to the internal team the way the edit affordances above are. A Default Reviewer who
    // holds the edit role is exactly the person who knows the finding is wrong — they own the
    // approval — and hiding the control from them would push the decision into a Slack message
    // where nothing is recorded (ORB-839).
    //
    // ⚑ And only where QA is shown at all (ORB-982). Belt and braces — a customer never reaches the
    // panel that carries the control — but this is the hook the override affordance actually asks,
    // so it is the honest place for the second condition rather than a comment promising the first
    // one holds.
    canOverrideQaFinding: canEdit && qaVisible,
    isCommentsDisabled,
    requireSignOffConfirmation: settings?.RequireSignOffConfirmation !== false,
    settingsLoaded,
  }
}
